import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { jobsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { MapPin, Briefcase, Calendar, ArrowRight, Search } from 'lucide-react';

const Container = styled.div`
  background: ${p => p.theme.colors.background.primary};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}15 0%, ${p => p.theme.colors.primary}08 100%);
  border-bottom: 1px solid ${p => p.theme.colors.border.light};
  padding: 3rem 1rem;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: ${p => p.theme.typography.fontWeight.bold};
  color: ${p => p.theme.colors.text.primary};
  margin: 0 0 1rem 0;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: ${p => p.theme.colors.text.secondary};
  margin: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
  flex: 1;
`;

const SearchBar = styled.div`
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  background: ${p => p.theme.colors.background.secondary};
  padding: 1rem;
  border-radius: ${p => p.theme.borderRadius.lg};
  border: 1px solid ${p => p.theme.colors.border.light};
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.fontSize.base};
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const JobCard = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.border.light};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${p => p.theme.colors.primary};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${p => p.theme.shadows.lg};
    border-color: ${p => p.theme.colors.primary};

    &::before {
      transform: scaleX(1);
    }
  }
`;

const JobTitle = styled.h3`
  margin: 0;
  color: ${p => p.theme.colors.text.primary};
  font-size: ${p => p.theme.typography.fontSize.xl};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  line-height: 1.3;
`;

const JobSummary = styled.p`
  margin: 0;
  color: ${p => p.theme.colors.text.secondary};
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid ${p => p.theme.colors.border.light};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${p => p.theme.colors.text.secondary};
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: ${p => p.theme.borderRadius.full};
  font-size: ${p => p.theme.typography.fontSize.xs};
  font-weight: ${p => p.theme.typography.fontWeight.medium};
  background: ${p => p.active ? '#10B98120' : '#EF444420'};
  color: ${p => p.active ? '#065F46' : '#991B1B'};
`;

const ViewButton = styled.button`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${p => p.theme.colors.primary};
  color: ${p => p.theme.colors.white};
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  font-size: ${p => p.theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${p => p.theme.colors.primary};
    opacity: 0.9;
    transform: translateX(4px);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${p => p.theme.colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.3;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${p => p.theme.colors.text.secondary};
`;

const PublicJobsList = () => {
  const { theme } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const res = await jobsAPI.getAll({ public: 1 });
        setJobs(res.data.data || res.data || []);
      } catch (error) {
        console.error('Failed to load jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.title?.toLowerCase().includes(query) ||
      job.summary?.toLowerCase().includes(query) ||
      job.description?.toLowerCase().includes(query) ||
      job.location?.toLowerCase().includes(query)
    );
  });

  const activeJobs = filteredJobs.filter(job => job.is_active);

  return (
    <Container>
      <PublicHeader theme={theme} />
      <Hero theme={theme}>
        <HeroContent>
          <HeroTitle theme={theme}>Join Our Team</HeroTitle>
          <HeroSubtitle theme={theme}>
            Explore exciting career opportunities and make a meaningful impact with us
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <Section theme={theme}>
        {activeJobs.length > 0 && (
          <SearchBar theme={theme}>
            <Search size={20} color={theme.colors.text.secondary} />
            <SearchInput
              type="text"
              placeholder="Search jobs by title, location, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              theme={theme}
            />
          </SearchBar>
        )}

        {loading ? (
          <LoadingState theme={theme}>Loading available positions...</LoadingState>
        ) : activeJobs.length === 0 ? (
          <EmptyState theme={theme}>
            <EmptyIcon>💼</EmptyIcon>
            <h3 style={{ margin: '0 0 0.5rem 0', color: theme.colors.text.primary }}>
              {searchQuery ? 'No jobs found' : 'No open positions at the moment'}
            </h3>
            <p style={{ margin: 0 }}>
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Check back soon for new opportunities'}
            </p>
          </EmptyState>
        ) : (
          <>
            <div style={{ marginBottom: '1.5rem', color: theme.colors.text.secondary }}>
              {filteredJobs.length === activeJobs.length
                ? `${activeJobs.length} ${activeJobs.length === 1 ? 'position' : 'positions'} available`
                : `Showing ${activeJobs.length} of ${filteredJobs.length} positions`}
            </div>
            <Grid>
              {activeJobs.map(job => (
                <JobCard
                  key={job.id}
                  theme={theme}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                >
                  <div>
                    <JobTitle theme={theme}>{job.title}</JobTitle>
                    {job.summary && (
                      <JobSummary theme={theme}>{job.summary}</JobSummary>
                    )}
                  </div>

                  <MetaRow theme={theme}>
                    {job.location && (
                      <MetaItem theme={theme}>
                        <MapPin size={16} />
                        <span>{job.location}</span>
                      </MetaItem>
                    )}
                    {job.employment_type && (
                      <MetaItem theme={theme}>
                        <Briefcase size={16} />
                        <span>{job.employment_type}</span>
                      </MetaItem>
                    )}
                    {job.deadline && (
                      <MetaItem theme={theme}>
                        <Calendar size={16} />
                        <span>Apply by {new Date(job.deadline).toLocaleDateString()}</span>
                      </MetaItem>
                    )}
                  </MetaRow>

                  <ViewButton theme={theme}>
                    <span>View Details & Apply</span>
                    <ArrowRight size={18} />
                  </ViewButton>
                </JobCard>
              ))}
            </Grid>
          </>
        )}
      </Section>
      <PublicFooter theme={theme} />
    </Container>
  );
};

export default PublicJobsList;
